import {
  Dialog,
  LoadingIndicator,
  useSpacing,
} from "@surya-digital/leo-reactjs-material-ui";
import { Stack } from "@mui/material";
import { PreviewEmailRPC } from "@pulse/pulse-rpcs/lib/email-cycle/previewEmailRPC";
import { EmailStatus } from "@pulse/pulse-rpcs/lib/types/emailStatus";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnexpectedErrorDialog } from "../../../../components/dialogs/UnexpectedErrorDialog";
import { useRouteNavigator } from "../../../../routes/RoutesHelper";
import { usePreviewEmailStore } from "../../stores/Hooks";
import { EmailTemplateContentRTE } from "../../../emailTemplates/components/inputs/EmailTemplateContentRTE";
import { ResendEmailDialog } from "./ResendEmailDialog";
import { useSignInStore } from "../../../auth/store/hook";
import { UserPrivileges } from "../../../store/user/UserPrivileges";
import { EmailCycleStatus } from "@pulse/pulse-rpcs/lib/email-cycle/emailCycleStatus";

interface PreviewEmailDialogProps {
  emailId: string;
  recipientStatus: EmailStatus.EmailStatus;
  cycleStatus: EmailCycleStatus.EmailCycleStatus;
  onClose: VoidFunction;
  onCloseAfterResendAttempt: VoidFunction;
}

type DialogToOpen =
  | "EMAIL_PREVIEW"
  | "RESEND_EMAIL"
  | "PREVIEW_UNEXPECTED_ERROR";

const PREVIEW_EMAIL_DIALOG_WIDTH = "960px";

export const PreviewEmailDialog: React.FC<PreviewEmailDialogProps> = observer(
  ({
    emailId,
    cycleStatus,
    recipientStatus,
    onClose,
    onCloseAfterResendAttempt,
  }) => {
    const [dialogToOpen, setDialogToOpen] =
      useState<DialogToOpen>("EMAIL_PREVIEW");

    const { t } = useTranslation();
    const spacing = useSpacing();
    const navigateTo = useRouteNavigator();

    const signInStore = useSignInStore();
    const previewEmailStore = usePreviewEmailStore();

    useEffect(() => {
      previewEmailStore
        .previewEmail(emailId)
        .catch(() => setDialogToOpen("PREVIEW_UNEXPECTED_ERROR"));

      return previewEmailStore.resetStore;
    }, [emailId, previewEmailStore]);

    useEffect(() => {
      if (!previewEmailStore.previewEmailRPCError) return;

      switch (previewEmailStore.previewEmailRPCError) {
        case PreviewEmailRPC.RPCError.InvalidEmailId:
          navigateTo.pageNotFound();
          break;
        case PreviewEmailRPC.RPCError.UnexpectedError:
          setDialogToOpen("PREVIEW_UNEXPECTED_ERROR");
          break;
        default:
          previewEmailStore.previewEmailRPCError satisfies never;
      }
    }, [navigateTo, previewEmailStore.previewEmailRPCError, previewEmailStore]);

    const disableResendEmailButton =
      !signInStore.isPrivilegeGranted(UserPrivileges.processEmailCycle) ||
      cycleStatus === EmailCycleStatus.EmailCycleStatus.QUEUED ||
      cycleStatus === EmailCycleStatus.EmailCycleStatus.IN_PROGRESS ||
      previewEmailStore.loadingPreviewEmail;

    return (
      <>
        <Dialog
          open={dialogToOpen === "EMAIL_PREVIEW"}
          title={t("emailCycles.previewEmail")}
          onClose={onClose}
          width={PREVIEW_EMAIL_DIALOG_WIDTH}
          showCloseButton
          contentPadding={spacing.spaceLG}
          secondaryButtonText={t("emailCycles.resendEmail")}
          onSecondaryButtonClick={(): void => setDialogToOpen("RESEND_EMAIL")}
          isSecondaryButtonDisabled={disableResendEmailButton}
          disableBackdropClick={true}
        >
          <Stack width="100%">
            {previewEmailStore.loadingPreviewEmail ? (
              <LoadingIndicator
                variant="container"
                loadingText={t("common.detailsLoadingState")}
                isLoading
              />
            ) : (
              <EmailTemplateContentRTE
                mode="preview"
                initialValue={previewEmailStore.emailContent.body.content}
                rteFileService={
                  previewEmailStore.emailContent.body.rteFileService
                }
                hidePreviewEditToggler
              />
            )}
          </Stack>
        </Dialog>

        {dialogToOpen === "RESEND_EMAIL" && (
          <ResendEmailDialog
            emailId={emailId}
            recipientStatus={recipientStatus}
            onCancel={(): void => setDialogToOpen("EMAIL_PREVIEW")}
            onCloseAfterResendAttempt={onCloseAfterResendAttempt}
          />
        )}

        {dialogToOpen === "PREVIEW_UNEXPECTED_ERROR" && (
          <UnexpectedErrorDialog
            message={t("emailCycles.previewEmailUnexpectedErrorMessage")}
            onClickDone={onClose}
          />
        )}
      </>
    );
  },
);
